import React from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Stack,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  PersonAddAlt1Outlined as PersonAddIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { canEditChildProfile, getRoleDisplay } from '../../../constants/roles';
import { TERMINOLOGY } from '../../../constants/terminology';
import colors from '../../../assets/theme/colors';
import { getChildProfileSetupStatus } from '../../../utils/profileCompletion';
import { ProfileSetupAvatar } from './ProfileSetupIndicator';
import { getChildAccentTheme } from './childAccentTheme';

const stripRolePrefix = (label = '') => String(label || '').replace(/^[^\w]+/, '').trim();

const getRoleLabel = (role) => {
  const label = getRoleDisplay(role)?.label || '';
  return stripRolePrefix(label) || 'Care Owner';
};

const getCareTeamSummary = (members = []) => {
  const names = members
    .map((member) => member?.displayName || member?.name || member?.email || '')
    .filter(Boolean);

  if (!names.length) {
    return 'Care circle not loaded yet';
  }

  return `Care circle: ${names.slice(0, 2).join(', ')}${names.length > 2 ? ` +${names.length - 2}` : ''}`;
};

export const ChildSwitcherTrigger = ({
  child,
  roleLabel = '',
  showRole = false,
  careTeamMembers = null,
  avatarSize = 36,
  onClick,
  sx = {},
}) => {
  if (!child) {
    return null;
  }
  const childAccent = getChildAccentTheme(child);
  const setupStatus = getChildProfileSetupStatus(
    child,
    Array.isArray(careTeamMembers) ? { careTeamMembers } : {}
  );

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flex: '1 1 auto',
        minWidth: 0,
        alignItems: 'center',
        gap: 0.75,
        px: 0,
        py: 0,
        minHeight: 40,
        borderRadius: 0,
        border: 'none',
        bgcolor: 'transparent',
        boxShadow: 'none',
        color: colors.landing.heroText,
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '-0.02em',
        justifyContent: 'flex-start',
        ...sx,
      }}
    >
      <ButtonBase
        onClick={onClick}
        data-cy="dashboard-child-switcher"
        sx={{
          minWidth: 0,
          flex: 1,
          display: 'flex',
          justifyContent: 'flex-start',
          borderRadius: 0,
          px: 0,
          py: 0,
          color: colors.landing.heroText,
          textAlign: 'left',
          '&:hover': {
            bgcolor: 'transparent',
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.85} sx={{ minWidth: 0, width: '100%' }}>
          <ProfileSetupAvatar
            child={child}
            setupStatus={setupStatus}
            size={avatarSize}
            ringWidth={3}
            accentColor={childAccent.primary}
            avatarSx={{
              bgcolor: childAccent.primary,
              color: colors.landing.surface,
              fontWeight: 700,
              boxShadow: 'none',
            }}
          />

          <Stack direction="column" alignItems="flex-start" spacing={0.1} sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                width: '100%',
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: showRole ? '0.98rem' : '1rem',
                  fontWeight: 700,
                  color: colors.landing.heroText,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.05,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  minWidth: 0,
                  flexShrink: 1,
                }}
              >
                {child.name}
              </Typography>

              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: colors.landing.textMuted, flexShrink: 0 }} />

            </Box>

            {showRole ? (
              <Typography
                component="span"
                sx={{
                  fontSize: '0.76rem',
                  fontWeight: 500,
                  color: colors.landing.textMuted,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.05,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                Your role: {roleLabel || 'Care Owner'}
              </Typography>
            ) : null}
        </Stack>
        </Stack>
      </ButtonBase>
    </Box>
  );
};

export const ChildSwitcherPanel = ({
  children = [],
  activeChildId,
  getUserRoleForChild,
  careTeamsByChildId = {},
  onSelectChild,
  onAddChild,
  showCareTeamSummary = false,
  showAddChild = null,
  title = 'Who are we logging for today?',
  subtitle = '',
  addChildLabel = `Add ${TERMINOLOGY.subject}`,
  currentLabel = 'Current',
  switchLabel = 'Switch',
  activeChild = null,
  stickyAddChild = false,
  showAddChildFooter = true,
  sx = {},
}) => {
  const canAddChild = typeof onAddChild === 'function'
    && (showAddChild === null ? true : Boolean(showAddChild));
  const renderRoleLabel = (childId) => getRoleLabel(getUserRoleForChild?.(childId));
  const activeProfileChild = activeChild || children.find((child) => child.id === activeChildId) || children[0] || null;
  const activeProfileId = activeChildId || activeProfileChild?.id || null;
  const visibleChildren = children.filter((child) => child.id);

  return (
    <Stack
      spacing={1}
      sx={{
        display: 'flex',
        minHeight: 0,
        ...sx,
      }}
    >
      <Box sx={{ flexShrink: 0, px: 0.75, pt: 0.25, pb: subtitle ? 0.5 : 0 }}>
        <Typography
          sx={{
            fontSize: '0.98rem',
            fontWeight: 700,
            textTransform: 'none',
            letterSpacing: '-0.02em',
            color: colors.landing.heroText,
            lineHeight: 1.15,
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ mt: 0.8, fontSize: '0.92rem', color: colors.landing.textMuted, lineHeight: 1.45 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: 0.75,
          pb: 0.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.landing.textMuted }}>
          Switch Profile
        </Typography>

        {canAddChild ? (
          <Button
            onClick={() => onAddChild?.()}
            size="small"
            startIcon={<PersonAddIcon sx={{ fontSize: 15, color: colors.brand.ink }} />}
            sx={{
              minWidth: 0,
              px: 1.05,
              py: 0.45,
              borderRadius: '9999px',
              border: `1px solid ${colors.brand.ink}`,
              bgcolor: alpha(colors.brand.ink, 0.06),
              color: colors.landing.heroText,
              fontSize: '0.72rem',
              fontWeight: 800,
              textTransform: 'none',
              boxShadow: 'none',
              flexShrink: 0,
              '&:hover': {
                bgcolor: alpha(colors.brand.ink, 0.1),
                borderColor: colors.brand.ink,
                boxShadow: 'none',
              },
            }}
            data-testid={stickyAddChild ? 'child-switcher-add-profile-inline' : 'child-switcher-add-profile-inline'}
          >
            + Add Profile
          </Button>
        ) : null}
      </Box>

      <Box
        sx={{
          overflowY: stickyAddChild ? 'auto' : 'visible',
          WebkitOverflowScrolling: stickyAddChild ? 'touch' : 'auto',
          pr: stickyAddChild ? 0.5 : 0,
          pb: stickyAddChild ? 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' : 0,
          maxHeight: stickyAddChild ? 'calc(90dvh - 8.5rem)' : 'none',
        }}
        data-testid={stickyAddChild ? 'child-switcher-scroll' : undefined}
      >
        <Stack spacing={1}>
          {visibleChildren.map((child, index) => {
          const isSelected = child.id === activeProfileId;
          const childAccent = getChildAccentTheme(child, index);
          const careTeamMembers = careTeamsByChildId[child.id] || [];
          const hasLoadedCareTeam = Boolean(
            child.id
            && Object.prototype.hasOwnProperty.call(careTeamsByChildId, child.id)
          );
          const role = getUserRoleForChild?.(child.id) || null;
          const roleLabel = renderRoleLabel(child.id);
          const canEditProfile = canEditChildProfile(role);
          const setupStatus = getChildProfileSetupStatus(
            child,
            hasLoadedCareTeam ? { careTeamMembers } : {}
          );

          return (
            <Button
              key={child.id}
              onClick={() => onSelectChild?.(child.id)}
              data-cy="dashboard-child-switch-option"
              fullWidth
              sx={{
                textTransform: 'none',
                alignItems: 'stretch',
                justifyContent: 'flex-start',
                p: 0,
                borderRadius: '16px',
                border: `1px solid ${isSelected ? alpha(childAccent.primary, 0.68) : colors.landing.borderLight}`,
                bgcolor: isSelected ? alpha(childAccent.primary, 0.08) : colors.landing.surface,
                boxShadow: `0 4px 12px ${colors.landing.shadowSoft}`,
                overflow: 'hidden',
                '&:hover': {
                  bgcolor: isSelected ? alpha(childAccent.primary, 0.11) : colors.landing.surfaceSoft,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', p: 1.25 }}>
                <ProfileSetupAvatar
                  child={child}
                  setupStatus={setupStatus}
                  size={40}
                  ringWidth={3}
                  mode={canEditProfile ? 'edit' : 'view'}
                  accentColor={childAccent.primary}
                  sx={{
                    boxShadow: isSelected ? `0 4px 12px ${alpha(childAccent.primary, 0.16)}` : 'none',
                  }}
                  avatarSx={{
                    bgcolor: childAccent.primary,
                    color: colors.landing.surface,
                    fontWeight: 700,
                    boxShadow: 'none',
                  }}
                />

                <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: colors.landing.heroText,
                      lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {child.name}
                  </Typography>

                  {showCareTeamSummary ? (
                    <Typography sx={{ mt: 0.35, fontSize: '0.82rem', color: colors.landing.textMuted, lineHeight: 1.35 }}>
                      {getCareTeamSummary(careTeamMembers)}
                    </Typography>
                  ) : null}

                  <Typography
                    sx={{
                      mt: 0.2,
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      color: isSelected ? childAccent.ink : colors.landing.textMuted,
                    }}
                  >
                    Your role: {roleLabel}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    flexShrink: 0,
                    px: 1,
                    py: 0.4,
                    borderRadius: '9999px',
                    bgcolor: isSelected ? childAccent.primary : colors.landing.sageLight,
                    color: isSelected ? colors.landing.surface : colors.landing.textMuted,
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  {isSelected ? currentLabel : switchLabel}
                </Box>
              </Box>
            </Button>
          );
        })}

        {visibleChildren.length === 0 ? (
          <Box sx={{ px: 0.75, py: 0.5 }}>
            <Typography sx={{ fontSize: '0.88rem', color: colors.landing.textMuted }}>
              No profiles yet.
            </Typography>
          </Box>
        ) : null}
        </Stack>
      </Box>

      {canAddChild && showAddChildFooter ? (
        <Box
          sx={{
            flexShrink: 0,
            pt: 1,
            pb: `calc(env(safe-area-inset-bottom, 0px) + ${stickyAddChild ? '0.75rem' : '0.25rem'})`,
            bgcolor: stickyAddChild ? colors.landing.surface : 'transparent',
            position: stickyAddChild ? 'sticky' : 'static',
            bottom: stickyAddChild ? 0 : 'auto',
            borderTop: stickyAddChild ? `1px solid ${colors.landing.borderLight}` : 'none',
            mt: stickyAddChild ? 0.5 : 0,
          }}
          data-testid={stickyAddChild ? 'child-switcher-add-profile-footer' : undefined}
        >
          <Button
            onClick={() => onAddChild?.()}
            fullWidth
            startIcon={<PersonAddIcon sx={{ fontSize: 18, color: colors.brand.ink }} />}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              p: 1.25,
              borderRadius: '16px',
              border: `1px dashed ${colors.landing.borderMedium}`,
              bgcolor: alpha(colors.brand.ink, 0.06),
              color: colors.landing.heroText,
              fontWeight: 800,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: alpha(colors.brand.ink, 0.09),
                borderColor: colors.brand.ink,
                boxShadow: 'none',
              },
            }}
          >
            {addChildLabel}
          </Button>
        </Box>
      ) : null}
    </Stack>
  );
};
